import { ArrowLeft, Grid3X3, MapPin, Plus, Save, Share2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from '@aimsos/i18n';
import {
  ActivityListWidget,
  Badge,
  hasCatalogWidget,
  NotesListWidget,
  Skeleton,
  Tooltip,
  useToast,
  Widget,
  WidgetCardShell,
  WidgetGrid,
  WidgetGridProvider,
} from '@aimsos/ui';
import { DASHBOARD_STATES, WIDGET_PREVIEW_DATA } from '../constants';
import { publishIssueLines } from '../utils/publishRejection.js';
import {
  CanvasWidget,
  DashboardButton,
  InlineEditableText,
  SelectLocationModal,
  ShareDashboardModal,
  WidgetLibraryModal,
} from '../components';
import { useDashboardEditor } from '../hooks/useDashboardEditor';
import { adaptWidgetsForGrid } from '../utils/widgetAdapter';

const DASHBOARD_LIST_PATH = '/dashboards';

/**
 * The two legacy seed types the catalogue registry does not draw.
 *
 * Everything else goes through `CanvasWidget`, which is the platform's one
 * answer to "what does type X look like" — the same resolver the widget
 * library's detail panel and the builder's live preview use. Before this the
 * canvas had its own pair of maps keyed on the old seed vocabulary
 * (`notes`, `tasks`, `pipeline`…), so a widget placed from the real catalogue
 * arrived as `KPI` or `METRIC_ROW`, matched neither map, and rendered an empty
 * card under a correct title.
 */
const LEGACY_PREVIEW_REGISTRY = {
  activity: ActivityListWidget,
  notes: NotesListWidget,
};

const DashboardNewPage = () => {
  const { t } = useTranslation('dashboards');
  const editor = useDashboardEditor();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [isWidgetLibraryModalOpen, setIsWidgetLibraryModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const toast = useToast();

  const {
    addWidgets,
    canAddWidgets,
    descriptionKey,
    description,
    id,
    loading,
    publish,
    removeWidget,
    saveDraft,
    saving,
    sectionId,
    sectionPath,
    state,
    sharedWith,
    titleKey,
    title,
    toggleWidgetLock,
    updateEditor,
    updateLayouts,
    widgets,
  } = editor;

  const showToast = useCallback(
    (severity, titleText, descriptionText = '') => {
      const opts = {
        title: titleText,
        message: descriptionText || undefined,
      };
      if (severity === 'success') toast.success(opts);
      else if (severity === 'error') toast.error(opts);
      else if (severity === 'warning') toast.warning(opts);
      else toast.info(opts);
    },
    [toast],
  );
  const closeLocationModal = useCallback(() => setIsLocationModalOpen(false), []);
  const closeWidgetLibraryModal = useCallback(() => setIsWidgetLibraryModalOpen(false), []);
  const closeShareModal = useCallback(() => setIsShareModalOpen(false), []);
  const handleShareModalClose = useCallback(() => setIsShareModalOpen(false), []);
  const openLocationModal = useCallback(() => setIsLocationModalOpen(true), []);
  const openWidgetLibraryModal = useCallback(() => setIsWidgetLibraryModalOpen(true), []);
  const handleBackToList = useCallback(() => navigate(DASHBOARD_LIST_PATH), [navigate]);

  // Close editor overlays when the route changes (shell nav, in-app links, another dashboard edit, etc.)
  useEffect(() => {
    queueMicrotask(() => {
      setIsWidgetLibraryModalOpen(false);
      setIsLocationModalOpen(false);
      setIsShareModalOpen(false);
    });
  }, [location.pathname, location.search]);
  const handleTitleChange = useCallback(
    (nextValue) => updateEditor({ title: nextValue, titleKey: '' }),
    [updateEditor],
  );
  const handleDescriptionChange = useCallback(
    (nextValue) => updateEditor({ description: nextValue, descriptionKey: '' }),
    [updateEditor],
  );

  const hasWidgets = widgets.length > 0;
  const resolvedTitle = titleKey ? t(titleKey, { defaultValue: title }) : title;
  const resolvedDescription = descriptionKey
    ? t(descriptionKey, { defaultValue: description })
    : description;
  const statusLabel = state === DASHBOARD_STATES.PUBLISHED ? t('status.published') : t('status.draft');
  const externalWidgetConfig = useMemo(
    () =>
      adaptWidgetsForGrid(widgets, {
        tooltips: {
          refresh: t('widgetActions.refresh'),
          expand: t('widgetActions.expand'),
          add: t('widgetActions.add'),
        },
      }),
    [t, widgets],
  );

  const handleSaveDraft = useCallback(async () => {
    if (saving) return;
    showToast('warning', t('editor.toasts.draftSaving'), t('editor.toasts.draftSavingDescription'));
    const result = await saveDraft();
    if (!result.success) {
      // The backend's own wording when it has one — "Section home-overview not
      // found" tells the author what to change; "could not save" does not.
      showToast(
        'error',
        t('editor.toasts.draftError'),
        result.error?.message || t('editor.toasts.draftErrorDescription'),
      );
      return;
    }
    // Saving cannot remove a placement: `update`'s composition is upsert-only
    // and the backend has no child-removal path this phase. Saying so beats
    // letting the author find the widget still there after a reload.
    if (result.notRemoved?.length) {
      showToast(
        'warning',
        t('editor.toasts.notRemovedTitle'),
        t('editor.toasts.notRemovedDescription', { count: result.notRemoved.length }),
      );
      return;
    }
    showToast('success', t('editor.toasts.draftSaved'), t('editor.toasts.draftSavedDescription'));
  }, [saveDraft, saving, showToast, t]);

  /*
    What a failed publish says. A rejection is `DSW022` and the backend
    attaches a report naming each offending instance; that wording is what
    tells the author what to change, so it is shown instead of the generic
    sentence. Anything else falls back to the backend's own message, and
    only then to ours (CT-5552).
  */
  const publishFailureMessage = useCallback(
    (result) => {
      if (result?.rejected) {
        const lines = publishIssueLines(result.issues);
        if (lines.length) {
          return t('editor.toasts.publishRejectedDescription', {
            count: lines.length,
            issues: lines.join(' · '),
          });
        }
      }
      return result?.error?.message || t('editor.toasts.publishErrorDescription');
    },
    [t],
  );

  const handlePublish = useCallback(async () => {
    if (saving) return;
    const hasTitle = Boolean(String(title || '').trim());
    const hasSection = Boolean(sectionId);
    if (!hasTitle || !hasSection) {
      const message = [
        !hasTitle ? t('editor.toasts.validationNoTitle') : null,
        !hasSection ? t('editor.toasts.validationNoSection') : null,
      ]
        .filter(Boolean)
        .join(' ');
      showToast('error', t('editor.toasts.validationTitle'), message);
      return;
    }
    showToast('warning', t('editor.toasts.publishing'), t('editor.toasts.publishingDescription'));
    const result = await publish();
    if (result?.validationErrors?.length) {
      const validationMessage = [
        result.validationErrors.includes('title') ? t('editor.toasts.validationNoTitle') : null,
        result.validationErrors.includes('sectionId') ? t('editor.toasts.validationNoSection') : null,
      ]
        .filter(Boolean)
        .join(' ');
      showToast('error', t('editor.toasts.validationTitle'), validationMessage);
      return;
    }
    if (!result?.success) {
      showToast('error', t('editor.toasts.publishError'), publishFailureMessage(result));
    }
  }, [publish, publishFailureMessage, saving, sectionId, showToast, t, title]);

  const renderWidget = useCallback(
    (widgetConfig) => {
      const builderType = widgetConfig._builderType;
      const cardConfig = widgetConfig.config?.card || {};

      let content = null;
      if (hasCatalogWidget(builderType)) {
        // A widget from the real catalogue. `CanvasWidget` resolves the type
        // and executes the stored definition, so the canvas draws exactly what
        // the library's preview and the builder's live preview drew — the same
        // renderer and the same data.
        content = (
          <CanvasWidget type={builderType} widgetUuid={widgetConfig._widgetUuid} />
        );
      } else {
        const previewData = WIDGET_PREVIEW_DATA[builderType] || null;
        const PreviewComponent = LEGACY_PREVIEW_REGISTRY[builderType];
        if (previewData && PreviewComponent) {
          content = <PreviewComponent data={previewData} isCompressed={false} />;
        } else if (previewData) {
          content = <Widget data={previewData} />;
        }
      }

      return (
        <WidgetCardShell
          title={cardConfig.title}
          headerButtons={cardConfig.header_buttons}
          isPinned={widgetConfig._locked}
          onPin={() => toggleWidgetLock(widgetConfig.instance_uuid)}
          onDelete={() => removeWidget(widgetConfig.instance_uuid)}
        >
          {content}
        </WidgetCardShell>
      );
    },
    [removeWidget, toggleWidgetLock],
  );
  const handleLocationSelect = useCallback(
    (selection) => {
      if (!selection) return;
      updateEditor({ sectionId: selection.id || null, sectionPath: selection.path || '' });
      showToast('success', t('editor.location.alertTitle'), t('editor.location.alertDescription'));
      closeLocationModal();
    },
    [closeLocationModal, showToast, t, updateEditor],
  );
  const handleWidgetLibraryConfirm = useCallback(
    (selectedWidgets) => {
      addWidgets(selectedWidgets);
      closeWidgetLibraryModal();
    },
    [addWidgets, closeWidgetLibraryModal],
  );
  const handleShareConfirm = useCallback(
    async (nextSharedWith) => {
      if (saving) return;
      const resolved = Array.isArray(nextSharedWith) ? nextSharedWith : [];
      updateEditor({ sharedWith: resolved });
      closeShareModal();
      // Sharing is its own action now: it no longer doubles as the gate the
      // Publish button had to pass through (CT-5552).
      showToast('success', t('editor.toasts.shared'), t('editor.toasts.sharedDescription'));
    },
    [closeShareModal, saving, showToast, t, updateEditor],
  );

  if (loading) {
    return (
      <main className="scrollbar-aimsos-auto flex h-full min-h-0 flex-1 flex-col overflow-y-auto bg-surface-base">
        <div className="flex min-h-0 flex-1 flex-col gap-6 p-6">
          <Skeleton height="56px" width="100%" borderRadius="8px" />
          <Skeleton height="40px" width="128px" borderRadius="8px" />
          <Skeleton height="224px" width="100%" borderRadius="8px" />
        </div>
      </main>
    );
  }

  return (
    <>
      <main className="scrollbar-aimsos-auto flex h-full min-h-0 flex-1 flex-col overflow-y-auto bg-surface-base">
        <div className="flex min-h-0 flex-1 flex-col gap-6 p-6">
          <header className="flex shrink-0 items-start justify-between gap-4">
            <div className="flex min-w-0 flex-1 items-start gap-2">
              <DashboardButton variant="ghost" size="sm" leftIcon={ArrowLeft} onClick={handleBackToList} />
              <div className="min-w-0 space-y-2">
                <div className="flex min-w-0 items-center gap-2">
                  <InlineEditableText
                    variant="title"
                    value={resolvedTitle}
                    placeholder={t('editor.newPage.untitled')}
                    onChange={handleTitleChange}
                  />
                  <Badge variant={state === DASHBOARD_STATES.PUBLISHED ? 'success' : 'default'}>{statusLabel}</Badge>
                  {sectionPath && (
                    <DashboardButton
                      variant="secondary"
                      size="sm"
                      leftIcon={MapPin}
                      className="h-7 max-w-[min(320px,50vw)] gap-1 px-2 py-1 font-normal"
                      onClick={openLocationModal}
                    >
                      <span className="truncate text-content-secondary">{sectionPath}</span>
                    </DashboardButton>
                  )}
                </div>
                <InlineEditableText
                  value={resolvedDescription}
                  placeholder={t('editor.newPage.addDescription')}
                  onChange={handleDescriptionChange}
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* All three wait for a save or a publish in flight — the
                  handlers already ignore a click then, and the buttons have to
                  say so instead of looking available (CT-5504). */}
              <Tooltip content={t('share.unavailableTooltip')} size="compact">
                <DashboardButton variant="ghost" size="sm" leftIcon={Share2} disabled />
              </Tooltip>
              <DashboardButton
                variant="ghost"
                size="sm"
                leftIcon={Save}
                onClick={handleSaveDraft}
                disabled={saving}
                aria-label={t('editor.newPage.saveDraftAria')}
              />
              <DashboardButton size="sm" onClick={handlePublish} disabled={saving}>
                {saving ? t('editor.newPage.publishingButton') : t('editor.newPage.publishButton')}
              </DashboardButton>
            </div>
          </header>

          <div className="flex shrink-0">
            <DashboardButton
              variant="secondary"
              size="sm"
              leftIcon={Plus}
              disabled={!canAddWidgets}
              onClick={openWidgetLibraryModal}
            >
              {t('editor.newPage.addWidgetButton')}
            </DashboardButton>
          </div>

          {!sectionPath && (
            <section className="flex min-h-0 w-full flex-1 flex-col items-center justify-center gap-6 rounded-lg border border-edge px-4 py-8">
              <div className="flex flex-col items-center gap-2 text-center">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-brand-50 text-brand-600 dark:bg-brand-950/40 dark:text-brand-400">
                  <MapPin size={18} />
                </span>
                <h2 className="text-base font-semibold text-content">{t('editor.newPage.chooseSectionTitle')}</h2>
                <p className="max-w-xl text-sm font-medium text-content-muted">
                  {t('editor.newPage.chooseSectionDescription')}
                </p>
              </div>
              <DashboardButton size="md" onClick={openLocationModal}>
                {t('editor.newPage.chooseButton')}
              </DashboardButton>
            </section>
          )}

          {sectionPath && !hasWidgets && (
            <section className="flex min-h-0 w-full flex-1 flex-col items-center justify-center gap-6 rounded-lg border border-edge px-4 py-8">
              <div className="flex flex-col items-center gap-2 text-center">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-brand-50 text-brand-600 dark:bg-brand-950/40 dark:text-brand-400">
                  <Grid3X3 size={18} />
                </span>
                <h2 className="text-base font-semibold text-content">{t('editor.emptyState.title')}</h2>
                <p className="max-w-xl text-sm font-medium text-content-muted">{t('editor.emptyState.description')}</p>
              </div>
              <DashboardButton
                size="md"
                disabled={!canAddWidgets}
                onClick={openWidgetLibraryModal}
                rightIcon={Plus}
              >
                {t('editor.emptyState.addButton')}
              </DashboardButton>
            </section>
          )}

          {hasWidgets && (
            <section className="flex min-h-[260px] flex-1 flex-col">
              <WidgetGridProvider isEditMode>
                <WidgetGrid
                  externalWidgetConfig={externalWidgetConfig}
                  onExternalLayoutChange={updateLayouts}
                  renderWidget={renderWidget}
                />
              </WidgetGridProvider>
            </section>
          )}
        </div>
      </main>

      <SelectLocationModal
        open={isLocationModalOpen}
        onClose={closeLocationModal}
        onSelect={handleLocationSelect}
      />

      <WidgetLibraryModal
        open={isWidgetLibraryModalOpen}
        onClose={closeWidgetLibraryModal}
        onConfirm={handleWidgetLibraryConfirm}
      />

      {isShareModalOpen && (
        <ShareDashboardModal
          open={isShareModalOpen}
          onClose={handleShareModalClose}
          onConfirm={handleShareConfirm}
          dashboardId={id || 'new'}
          dashboardName={resolvedTitle}
          existingSharedWith={sharedWith}
        />
      )}
    </>
  );
};

export default DashboardNewPage;
